import React, { useState, useMemo } from 'react';
import {
  Container,
  Grid,
  Card,
  Text,
  Title,
  Badge,
  Group,
  Stack,
  Paper,
  ThemeIcon,
  ScrollArea,
  Table,
  ActionIcon,
  Center,
  Loader,
  Select,
  TextInput,
  Tabs,
  Divider,
  Modal,
} from '@mantine/core';
import {
  IconDatabase,
  IconRefresh,
  IconClipboardData,
  IconChevronRight,
  IconCalendar,
  IconAdjustmentsCog as IconCog,
  IconTools,
  IconChartBar as IconStatistic,
  IconCurrencyDollar as IconCurrency,
  IconSearch,
  IconEye,
} from '@tabler/icons-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import useAuthenticatedData from '../../hooks/useAuthenticatedData';
import config from '../../config';

const REGION_COLORS = { EMEA: '#667eea', APAC: '#f093fb', 'North America': '#4facfe', 'South America': '#fa709a' };

const FleetAnalyzer = () => {
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [selectedPart, setSelectedPart] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('loads');
  const [partDetailModal, setPartDetailModal] = useState(false);
  const [sortBy, setSortBy] = useState('loadDate');
  const [filterPlatform, setFilterPlatform] = useState('');

  const { data: loads, isLoading: loadingLoads, refetch: refetchLoads } = useAuthenticatedData(
    `${config.apiBaseUrl}/fleetanalyzer/loads`
  );

  const { data: loadDetail } = useAuthenticatedData(
    selectedLoad ? `${config.apiBaseUrl}/fleetanalyzer/loads/${selectedLoad.id}` : null
  );
  const { data: loadParts } = useAuthenticatedData(
    selectedLoad ? `${config.apiBaseUrl}/fleetanalyzer/loads/${selectedLoad.id}/parts` : null
  );

  const { data: searchResults, isLoading: loadingSearch } = useAuthenticatedData(
    searchQuery.length >= 2
      ? `${config.apiBaseUrl}/fleetanalyzer/parts/search?searchTerm=${encodeURIComponent(searchQuery)}`
      : null
  );

  const { data: partDetail, isLoading: loadingPartDetail } = useAuthenticatedData(
    selectedPart ? `${config.apiBaseUrl}/fleetanalyzer/parts/${selectedPart.id}` : null
  );

  const filteredLoads = useMemo(() => {
    if (!loads) return [];
    let arr = loads;
    if (filterPlatform) arr = arr.filter(l => l.platform === filterPlatform);
    if (sortBy === 'loadDate') arr = arr.sort((a, b) => new Date(b.loadDate) - new Date(a.loadDate));
    if (sortBy === 'platform') arr = arr.sort((a, b) => a.platform.localeCompare(b.platform));
    if (sortBy === 'parts') arr = arr.sort((a, b) => b.totalParts - a.totalParts);
    return arr;
  }, [loads, filterPlatform, sortBy]);

  const platforms = useMemo(() => (loads ? [...new Set(loads.map(l => l.platform))] : []), [loads]);

  const formatDate = d => new Date(d).toLocaleDateString();
  const formatCurrency = v => (v != null ? `$${Math.round(v).toLocaleString()}` : 'N/A');

  const openPartDetail = part => {
    setSelectedPart(part);
    setPartDetailModal(true);
  };

  const formatValueRangeData = () =>
    partDetail
      ? ['NE', 'OH', 'SV', 'AR'].map(c => ({ condition: c, value: partDetail[`fmv_${c}`] || 0 })).filter(x => x.value)
      : [];

  const formatRegionDemandData = () => {
    if (!partDetail?.regionDemand) return [];
    return [
      { name: 'EMEA', value: partDetail.regionDemand.totalEMEAInq },
      { name: 'APAC', value: partDetail.regionDemand.totalAPACInq },
      { name: 'North America', value: partDetail.regionDemand.totalNorthAmericaInq },
      { name: 'South America', value: partDetail.regionDemand.totalSouthAmericaInq },
    ].filter(r => r.value);
  };

  return (
    <Container size="xl" p="md" mt="lg">
      <Card mb="lg" p="lg" withBorder>
        <Group justify="space-between">
          <Group>
            <ThemeIcon size="xl" radius="xl" variant="light" color="blue">
              <IconDatabase size={28} />
            </ThemeIcon>
            <Stack gap={0}>
              <Title order={2}>Fleet Analyzer</Title>
              <Text c="dimmed">Analyzer Loads & Parts</Text>
            </Stack>
          </Group>
          <ActionIcon variant="light" onClick={refetchLoads}>
            <IconRefresh />
          </ActionIcon>
        </Group>
      </Card>

      <Tabs value={activeTab} onChange={setActiveTab} color="blue">
        <Tabs.List grow>
          <Tabs.Tab value="loads" leftSection={<IconClipboardData size={16} />}>Loads</Tabs.Tab>
          <Tabs.Tab value="search" leftSection={<IconSearch size={16} />}>Search</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="loads" pt="md">
          <Grid>
            <Grid.Col span={4}>
              <Card withBorder style={{ height: '80vh', overflow: 'hidden' }}> 
                <Group justify="space-between" mb="sm">
                  <Select
                    placeholder="Platform"
                    value={filterPlatform}
                    onChange={setFilterPlatform}
                    data={[{ value: '', label: 'All' }, ...platforms.map(p => ({ value: p, label: p }))]}
                    size="sm"
                  />
                  <Select
                    placeholder="Sort by"
                    value={sortBy}
                    onChange={setSortBy}
                    data={[
                      { value: 'loadDate', label: 'Date' },
                      { value: 'platform', label: 'Platform' },
                      { value: 'parts', label: 'Parts' },
                    ]}
                    size="sm"
                  />
                </Group>
                <ScrollArea style={{ height: '72vh' }}>
                  {loadingLoads ? (
                    <Center><Loader /></Center>
                  ) : (
                    filteredLoads.map(load => (
                      <Paper
                        key={load.id}
                        p="sm"
                        mb="sm"
                        withBorder
                        style={{ 
                          cursor: 'pointer',
                          backgroundColor: selectedLoad?.id === load.id ? 'var(--mantine-color-blue-light)' : undefined
                        }}
                        onClick={() => setSelectedLoad(load)}
                      >
                        <Group justify="space-between">
                          <div>
                            <Badge variant="light">{load.platform}</Badge>
                            <Text size="sm">{formatDate(load.loadDate)}</Text>
                          </div>
                          <IconChevronRight size={16} />
                        </Group>
                      </Paper>
                    ))
                  )}
                </ScrollArea>
              </Card>
            </Grid.Col>

            <Grid.Col span={8}>
              {selectedLoad ? (
                <Stack gap="md">
                  <Grid>
                    <Grid.Col span={3}>
                      <Card withBorder bg="blue.1">
                        <Stack align="center" gap="xs">
                          <ThemeIcon color="blue" variant="light">
                            <IconCog size={16} />
                          </ThemeIcon>
                          <Text size="sm">Total Parts</Text>
                          <Text fw={700}>{selectedLoad.totalParts}</Text>
                        </Stack>
                      </Card>
                    </Grid.Col>
                    <Grid.Col span={3}>
                      <Card withBorder bg="pink.1">
                        <Stack align="center" gap="xs">
                          <ThemeIcon color="pink" variant="light">
                            <IconTools size={16} />
                          </ThemeIcon>
                          <Text size="sm">Repairable%</Text>
                          <Text fw={700}>{Math.round((selectedLoad.repairableParts/selectedLoad.totalParts)*100)}%</Text>
                        </Stack>
                      </Card>
                    </Grid.Col>
                    <Grid.Col span={3}>
                      <Card withBorder bg="cyan.1">
                        <Stack align="center" gap="xs">
                          <ThemeIcon color="cyan" variant="light">
                            <IconStatistic size={16} />
                          </ThemeIcon>
                          <Text size="sm">High Value</Text>
                          <Text fw={700}>{selectedLoad.highValueParts}</Text>
                        </Stack>
                      </Card>
                    </Grid.Col>
                    <Grid.Col span={3}>
                      <Card withBorder bg="violet.1">
                        <Stack align="center" gap="xs">
                          <ThemeIcon color="violet" variant="light">
                            <IconCurrency size={16} />
                          </ThemeIcon>
                          <Text size="sm">Avg Value</Text>
                          <Text fw={700}>{formatCurrency(selectedLoad.averagePartValue)}</Text>
                        </Stack>
                      </Card>
                    </Grid.Col>
                  </Grid>

                  <Grid>
                    <Grid.Col span={6}>
                      <Card withBorder>
                        <Title order={4} mb="md">Top Chapters</Title>
                        <ResponsiveContainer height={200} width="100%">
                          <BarChart data={loadDetail?.topATAChapters || []}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="chapterNumber" />
                            <YAxis />
                            <ChartTooltip />
                            <Bar dataKey="partCount" fill="var(--mantine-color-blue-6)" />
                          </BarChart>
                        </ResponsiveContainer>
                      </Card>
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <Card withBorder>
                        <Title order={4} mb="md">Parts Distribution</Title>
                        <ResponsiveContainer height={200} width="100%">
                          <PieChart>
                            <Pie
                              data={[
                                { name: 'Repairable', value: selectedLoad.repairableParts },
                                { name: 'Non-Repairable', value: selectedLoad.totalParts - selectedLoad.repairableParts },
                              ]}
                              outerRadius={80}
                              fill="#8884d8"
                              label
                            >
                              <Cell fill="var(--mantine-color-green-6)" />
                              <Cell fill="var(--mantine-color-red-6)" />
                            </Pie>
                            <ChartTooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </Card>
                    </Grid.Col>
                  </Grid>

                  <Card withBorder>
                    <Title order={4} mb="md">Parts in Load</Title>
                    <ScrollArea>
                      <Table highlightOnHover>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Part#</Table.Th>
                            <Table.Th>Description</Table.Th>
                            <Table.Th>Value NE</Table.Th>
                            <Table.Th>Repairable</Table.Th>
                            <Table.Th>Actions</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {loadParts?.slice(0,20).map(p => (
                            <Table.Tr key={p.id}>
                              <Table.Td>{p.partNumber}</Table.Td>
                              <Table.Td>{p.description}</Table.Td>
                              <Table.Td>{formatCurrency(p.fmv_NE)}</Table.Td>
                              <Table.Td>{p.repairable==='Y'?'Yes':'No'}</Table.Td>
                              <Table.Td>
                                <ActionIcon variant="light" onClick={()=>openPartDetail(p)}>
                                  <IconEye size={16} />
                                </ActionIcon>
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </ScrollArea>
                  </Card>
                </Stack>
              ) : (
                <Center style={{ height: '70vh' }}>
                  <Text c="dimmed">Select a load to view details</Text>
                </Center>
              )}
            </Grid.Col>
          </Grid>
        </Tabs.Panel>

        {/* Search Panel here (unchanged) */}
      </Tabs>

      {/* Part Detail Modal (unchanged) */}
    </Container>
  );
};

export default FleetAnalyzer;